import { useMemo, useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Typography, styled } from "@mui/material";
import SearchItem from "components/SearchItem";
import ExportButton from "components/button/ExportButton";
import useQuotationList from "features/quotation/hooks/useQuotationList";
import { getUserStorage } from "util/local_storage";
import routes from "constants/routes";
import { SESSION_KEY } from "constants/key";
import { TOption } from "type";
import useScrollToError from "hooks/useScrollToError";

interface HandleLinkClickProps {
  path: string;
}

const SelectButton = styled(Button)(() => ({
  padding: "10px 20px",
  border: "1px solid var(--silver-500)",
  width: "235px",
  fontWeight: "medium",
  color: "var(--gray-700)",
  borderRadius: "10px",
}));

const Title = styled(Box)(() => ({
  backgroundColor: "var(--primary-500)",
  display: "flex",
  alignItems: "center",
  fontSize: "20px",
  fontWeight: "bold",
  color: "var(--black)",
  paddingLeft: "35px",
}));

const SectionContent = styled("section")(() => ({
  padding: "0 5%",
}));

const tireTypeOptions = [
  { label: "夏タイヤ", value: "s" },
  { label: "冬タイヤ", value: "w" },
  { label: "オールシーズン", value: "a" },
];

/**
 * Component for creating a new quote.
 */
function NewQuote() {
  const { quoteByList } = useQuotationList();
  const user = getUserStorage();
  const [params, setParams] = useState({
    shopName: user?.storeName,
    quoteBy: "",
    tireType: "s",
  });

  useEffect(() => {
    if (quoteByList.length > 0 && params.quoteBy === "") {
      setParams((prev) => ({ ...prev, quoteBy: String(quoteByList[0].value) }));
    }
  }, [quoteByList]);
  const [quoteByError, setQuoteByError] = useState(false);
  const navigate = useNavigate();
  const quoteByInputRef = useRef<HTMLDivElement>(null);
  const scrollToQuoteBy = useScrollToError(quoteByInputRef, {
    inputSelector: 'input[type="text"], input[aria-autocomplete="list"]',
  });

  // ---------- Shared styles and configs (memoized) ----------
  const labelMinWidth = 175;
  const sectionGapSx = useMemo(
    () => ({ display: "flex", gap: 2, flexWrap: { sm: "wrap", xl: "nowap" } }),
    [],
  );
  const titleSizeSx = useMemo(
    () => ({ fontSize: { xs: 24, md: 28, lg: 35 }, fontWeight: "regular" }),
    [],
  );
  const titleBoxBaseSx = useMemo(
    () => ({ alignItems: "center", gap: 2, color: "var(--black)", whiteSpace: "nowrap" }),
    [],
  );
  const titleHeightSx = useMemo(() => ({ height: { xs: "60px", md: "70px", lg: "88px" } }), []);
  const selectButtonBaseSx = useMemo(
    () => ({ padding: "10px", textAlign: "left", marginTop: { sm: "15px", md: 0 }, fontSize: { xs: 16, md: 18, lg: 20 }, height: { xs: "44px", md: "55px", lg: "75px" } }),
    [],
  );
  const selectButtonActiveSx = useMemo(
    () => ({ color: "var(--gray-700)", border: "1px solid var(--gray-250)" }),
    [],
  );

  // ---------- Button configs ----------
  const phase1Buttons = useMemo(
    () => [
      {
        label: "車種から選ぶ",
        path: routes.mstCarModelSelection,
        width: { sm: "100%", md: "250px" },
      },
      {
        label: "タイヤサイズから選ぶ",
        path: routes.mstCarTireSizeSearch,
        width: { sm: "100%", md: "330px" },
      },
      {
        label: "商品コードから選ぶ",
        path: routes.mstCarModelProductCode,
        width: { sm: "100%", md: "330px" },
      },
    ],
    [],
  );
  const phase2Buttons = useMemo(
    () => [
      {
        label: "車種から選ぶ",
        path: routes.mstCarModelTireWheelSelection,
        width: { sm: "100%", md: "250px" },
      },
      {
        label: "商品コードから選ぶ",
        path: routes.mstCarWheelProductCode,
        width: { sm: "100%", md: "330px" },
      },
    ],
    [],
  );

  // Calculate autocomplete value: use option from list if found, otherwise use string directly (freeSolo)
  const quoteByValue = useMemo(() => {
    if (!params.quoteBy) return null;
    const foundOption = quoteByList.find((option) => option.label === params.quoteBy || option.value === params.quoteBy);
    return foundOption || params.quoteBy;
  }, [params.quoteBy, quoteByList]);

  const handleLinkClick = (path: HandleLinkClickProps["path"]): void => {
    // if (!responsiblePerson || !tireType) {
    //   setAlert("担当者名とタイヤの種類を選択してください。");
    //   return;
    // }
    if (!params.quoteBy) {
      setQuoteByError(true);
      scrollToQuoteBy();
      return;
    }
    sessionStorage.setItem(SESSION_KEY.QUOTE_BY, params.quoteBy);
    sessionStorage.setItem(SESSION_KEY.SELECTED_PAGE, path);
    sessionStorage.setItem(SESSION_KEY.TIRE_TYPE, params.tireType);
    navigate(path);
  };

  return (
    <>
      <Box sx={{ px: { xs: 2, md: 2.5, lg: 5 }, pt: { xs: 2, md: 2.5, lg: 3 } }}>
        <Button onClick={() => navigate(-1)} variant="custom-outlined">
          <Box>＜ 戻る</Box>
        </Button>
      </Box>
      <Box sx={{ pt: 6, paddingX: { md: "0px", lg: "60px" }, display: { sm: "block", lg: "flex" } }}>
      <Box sx={{ marginLeft: { sm: "30px", lg: 0 } }}>
        <Typography
          sx={{
            fontWeight: "medium",
            color: "var(--gray-700)",
            fontSize: "29px",
            marginBottom: "22px",
          }}
        >
          新規見積
        </Typography>
        <ExportButton
          label="見積一覧"
          onClick={() => {
            navigate("/quote-list");
          }}
        />
      </Box>
      <Box sx={{ padding: 2, fontFamily: "Arial, sans-serif", flex: 2 }}>
        <SectionContent>
          <Typography variant="h2" sx={{ ...titleSizeSx, marginBottom: "10px" }}>
            新規見積を作成します
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              marginBottom: "70px",
              flexWrap: "wrap",
              alignItems: "start",
            }}
          >
            <SearchItem
              label="店舗名"
              placeholder="店舗名"
              type="input"
              value={params.shopName || ""}
              disabled
              containerSx={{ width: { sm: "100%", md: "auto" } }}
              labelSx={{ minWidth: `${labelMinWidth}px` }}
              valueSx={{ width: "100%" }}
            />
            <Box sx={{ width: { xs: "100%", md: "auto" } }} ref={quoteByInputRef}>
              <SearchItem
                label="見積作成者"
                type="autocomplete"
                autocompleteOptions={quoteByList}
                freeSolo
                value={quoteByValue}
                error={quoteByError}
                onChange={(v) => {
                  const newLabel = typeof v === "string" ? v : (v && (v as TOption).label) || "";
                  setParams((prev) => ({ ...prev, quoteBy: newLabel }));
                  setQuoteByError(false);
                }}
                containerSx={{ width: { sm: "100%", xl: "auto" } }}
                labelSx={{ minWidth: `${labelMinWidth}px` }}
                valueSx={{ width: "100%" }}
              />
              {quoteByError && (
                <Typography
                  sx={{
                    color: "var(--red-800)",
                    fontSize: { xs: 16, md: 18, lg: 20 },
                    marginTop: "4px",
                    marginLeft: { xs: 0, sm: `${labelMinWidth + 10}px` },
                  }}
                >
                  見積担当者を入力してください
                </Typography>
              )}
            </Box>
          </Box>
        </SectionContent>

        <SectionContent style={{ marginBottom: "50px" }}>
          <Typography variant="h2" sx={{ ...titleSizeSx, marginBottom: "10px" }}>
            見積するタイヤの選択をします
          </Typography>
          <Box sx={{ ...sectionGapSx, alignItems: "center" }}>
            <Title
              sx={{
                ...titleBoxBaseSx,
                ...titleHeightSx,
                width: { sm: "100%", xl: "165px" },
                fontSize: { xs: 16, md: 18, lg: 20 },
                fontWeight: "bold",
              }}
            >
              タイヤ種類
            </Title>
            <Box
              sx={{
                width: { sm: "100%", xl: "auto" },
                display: { sm: "block", md: "flex" },
                gap: 1,
                justifyContent: "center",
                alignItems: "end",
              }}
            >
              {tireTypeOptions.map((type) => (
                <SelectButton
                  key={type.value}
                  variant="outlined"
                  onClick={() => setParams((prev) => ({ ...prev, tireType: type.value }))}
                  sx={{
                    backgroundColor:
                      params.tireType === type.value ? "var(--blue-400)" : "var(--white)",
                    width: { sm: "100%", md: "235px" },
                    ...selectButtonActiveSx,
                    ...selectButtonBaseSx,
                  }}
                >
                  {type.label}
                </SelectButton>
              ))}
            </Box>
          </Box>
        </SectionContent>

        <hr />

        <SectionContent style={{ marginTop: "36px" }}>
          <Typography variant="h2" sx={{ ...titleSizeSx, marginBottom: "45px" }}>
            見積種類と見積方法を選択します
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                gap: 2,
                marginBottom: "82px",
                flexWrap: { sm: "wrap", xl: "nowap" },
                alignItems: "center",
              }}
            >
              <Title
                sx={{
                  ...titleBoxBaseSx,
                  paddingLeft: "60px",
                  ...titleHeightSx,
                  width: { sm: "100%", xl: "325px" },
                  fontSize: { xs: 16, md: 18, lg: 20 },
                  fontWeight: "bold",
                  position: "relative",
                }}
              >
                <img
                  src="/img/features/quote/tire-svg.svg"
                  alt="タイヤ単品"
                  style={{ height: "95px", position: "absolute", left: "-35px" }}
                />
                タイヤ単品の見積
              </Title>
              <Box
                sx={{
                  display: { sm: "block", md: "flex" },
                  gap: 1,
                  alignItems: "center",
                  width: { sm: "100%", xl: "auto" },
                  justifyContent: "center",
                }}
              >
                {phase1Buttons.map((b) => (
                  <SelectButton
                    key={b.label}
                    variant="outlined"
                    onClick={() => handleLinkClick(b.path)}
                    sx={{ width: b.width, ...selectButtonBaseSx }}
                  >
                    {b.label}
                  </SelectButton>
                ))}
              </Box>
            </Box>

            {/* Phase 2 */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                gap: 2,
                marginBottom: "82px",
                flexWrap: { sm: "wrap", xl: "nowap" },
              }}
            >
              <Title
                sx={{
                  ...titleBoxBaseSx,
                  paddingLeft: "60px",
                  ...titleHeightSx,
                  width: { sm: "100%", xl: "325px" },
                  fontSize: { xs: 16, md: 18, lg: 20 },
                  fontWeight: "bold",
                  position: "relative",
                }}
              >
                <img
                  src="/img/features/quote/wheel-svg.svg"
                  alt="タイヤ&ホイールセット"
                  style={{ height: "95px", position: "absolute", left: "-35px" }}
                />
                タイヤホイールセットの見積
              </Title>
              <Box sx={{ width: { sm: "100%", xl: "auto" } }}>
                <Box
                  sx={{
                    display: { sm: "block", md: "flex" },
                    gap: 1,
                    alignItems: "center",
                    marginTop: "6.5px",
                    width: { sm: "100%", xl: "auto" },
                    justifyContent: { sm: "center", xl: "left" },
                  }}
                >
                  {phase2Buttons.map((b) => (
                    <SelectButton
                      key={b.label}
                      variant="outlined"
                      onClick={() => handleLinkClick(b.path)}
                      sx={{ width: b.width, ...selectButtonBaseSx }}
                    >
                      {b.label}
                    </SelectButton>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </SectionContent>
      </Box>
    </Box>
    </>
  );
}

export default NewQuote;
